// src/components/Admin/SeasonFormModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import type { Season } from '../../types/series';
import seriesService from '../../services/seriesService';
import { useToast } from '../../hooks/useToast';

interface SeasonFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  season?: Season;
  seriesId?: string;
  onClose: () => void;
  onSave: (season: Season) => void;
}

const SeasonFormModal: React.FC<SeasonFormModalProps> = ({
  isOpen,
  mode,
  season,
  seriesId,
  onClose,
  onSave,
}) => {
  const { showError, showSuccess } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    seasonNumber: 1,
    releaseDate: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'COMPLETED',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when modal opens or season changes
  useEffect(() => {
    if (season && (mode === 'edit' || mode === 'view')) {
      setFormData({
        title: season.title || '',
        description: season.description || '',
        seasonNumber: season.seasonNumber || 1,
        releaseDate: season.releaseDate ? season.releaseDate.split('T')[0] : '', // Convert to date input format
        status: season.status,
      });
    } else if (mode === 'add') {
      setFormData({
        title: '',
        description: '',
        seasonNumber: 1,
        releaseDate: '',
        status: 'DRAFT',
      });
    }
    setErrors({});
  }, [season, mode, isOpen]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.seasonNumber || formData.seasonNumber < 1) {
      newErrors.seasonNumber = 'Season number must be 1 or greater';
    }

    if (!seriesId && mode === 'add') {
      newErrors.general = 'Series ID is required to create a season';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;

    if (!validateForm()) {
      return;
    }

    if (!seriesId) {
      showError('Series ID is required');
      return;
    }

    setIsLoading(true);
    try {
      let savedSeason: Season;
      
      if (mode === 'add') {
        savedSeason = await seriesService.createSeason(seriesId, formData);
        showSuccess(`Season "${formData.title}" created successfully`);
      } else if (mode === 'edit' && season) {
        savedSeason = await seriesService.updateSeason(seriesId, season.id, formData);
        showSuccess(`Season "${formData.title}" updated successfully`);
      } else {
        throw new Error('Invalid mode or missing season data');
      }

      onSave(savedSeason);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save season';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', description: 'Not published yet' },
    { value: 'PUBLISHED', label: 'Published', description: 'Live and visible to users' },
    { value: 'ARCHIVED', label: 'Archived', description: 'Hidden from public view' },
    { value: 'COMPLETED', label: 'Completed', description: 'Season is finished' },
  ];

  const getModalTitle = () => {
    switch (mode) {
      case 'add':
        return 'Create New Season';
      case 'edit':
        return `Edit: ${season?.title || 'Season'}`;
      case 'view':
        return `View: ${season?.title || 'Season'}`;
      default:
        return 'Season Form';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter season title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={mode === 'view'}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter season description"
            />
          </div>

          {/* Season Number and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Season Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Season Number *
              </label>
              <input
                type="number"
                min="1"
                value={formData.seasonNumber}
                onChange={(e) => handleInputChange('seasonNumber', parseInt(e.target.value) || 1)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                placeholder="1"
              />
              {errors.seasonNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.seasonNumber}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {statusOptions.find(opt => opt.value === formData.status)?.description}
              </p>
            </div>
          </div>

          {/* Release Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Date
            </label>
            <input
              type="date"
              value={formData.releaseDate}
              onChange={(e) => handleInputChange('releaseDate', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
            />
            <p className="mt-1 text-sm text-gray-500">
              Optional: Set when this season will be released
            </p>
          </div>

          {/* Show existing season info for view/edit mode */}
          {(mode === 'edit' || mode === 'view') && season && (
            <div className="bg-gray-50 rounded-lg p-4 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Season Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Episodes: </span>
                  <span className="text-gray-600">{season.totalEpisodes || 0}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Released: </span>
                  <span className="text-gray-600">{season.isReleased ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created: </span>
                  <span className="text-gray-600">
                    {new Date(season.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Updated: </span>
                  <span className="text-gray-600">
                    {new Date(season.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || !formData.title.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {mode === 'add' ? 'Create Season' : 'Save Changes'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonFormModal;